import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalyticsContext } from '../../../../context/analytics-context';
import { useInitializationDataContext } from '../../../../context/data-context';
import { FabricTextBox } from '../../../../global-types';
import { useActiveCanvas } from '../../../../hooks';
import {
  getGroupedTextObject,
  CanvasDataTypes,
  convertPointToPixel,
  convertPixelToPoint,
  getFontName,
} from '../../../../utils';
import { Font, FontSelectedOption } from '../font-drawer-types';
import styles from '../font-drawer.module.scss';
import { ALIGNMENT_OPTIONS } from '../utils/constants';
import { findKeyByValue } from '../utils/find-key-by-value';
import { formatFontCollection } from '../utils/format-font-collection';
import { getLabelByFontId } from '../utils/get-label-by-fontid';
import { Alignment } from './alignment';
import { FontSize } from './font-size';
import { FontStyle } from './font-style';
import { useHistoryTransaction, useHistoryTransactionErrorToast } from '../../../../features/edit-history/edit-history';

export const FontTab = (): React.ReactElement => {
  const canvas = useActiveCanvas();
  const textObject: fabric.Textbox = getGroupedTextObject(canvas?.current) as fabric.Textbox;
  const { t } = useTranslation();
  const runHistoryTransaction = useHistoryTransaction();
  const { updateEditFormats } = useAnalyticsContext();
  const showHistoryTransactionError = useHistoryTransactionErrorToast();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();

  const [fonts, setFonts] = useState<Font[]>([]);
  const [alignmentIndex, setAlignmentIndex] = useState(findKeyByValue(textObject?.textAlign));

  const originalFontSize = textObject?.fontSize ? convertPixelToPoint(textObject.fontSize) : 30;
  const originalFontFamily = textObject?.fontFamily
    ? getLabelByFontId(textObject?.fontFamily, initializedData?.font_collection.fonts)
    : 'Hey Sunshine';
  const [fontSizeValue, setFontSizeValue] = useState(originalFontSize);
  const [selectedFont, setSelectedFont] = useState(originalFontFamily);
  const activeObject = canvas?.current?.getActiveObject() as FabricTextBox;
  const isActiveObjectValid = activeObject && activeObject.data;

  const shouldShowFontResizeControls =
    isActiveObjectValid &&
    (activeObject.CanEditFontSize ||
      activeObject.data.type === CanvasDataTypes.UserText ||
      activeObject.data.type === CanvasDataTypes.Placeholder);

  const updateCanvasAndFireChange = () => {
    canvas?.current?.renderAll();
    textObject.fire('changed');
  };

  const applyTextOptions = async (
    options: Partial<Pick<fabric.Textbox, 'fontFamily' | 'fontSize' | 'textAlign'>>,
  ): Promise<boolean> => {
    const targetCanvas = canvas?.current;
    if (!textObject || !targetCanvas) return false;

    try {
      await runHistoryTransaction(targetCanvas, () => {
        textObject.setOptions(options);
        updateCanvasAndFireChange();
      });
      return true;
    } catch {
      showHistoryTransactionError();
      return false;
    }
  };

  const handleFontStyleChange = async (option: FontSelectedOption) => {
    if (textObject) {
      const wasApplied = await applyTextOptions({ fontFamily: getFontName(option.id) });
      if (!wasApplied) return;
      updateEditFormats({ fontName: getFontName(option.id) });
      setSelectedFont(option.label);
    }
  };

  const handleFontSizeChange = async (action: string) => {
    const nextSize = fontSizeValue + (action === 'increase' ? 1 : -1);
    const wasApplied = await applyTextOptions({ fontSize: convertPointToPixel(nextSize) });
    if (!wasApplied) return;
    setFontSizeValue(nextSize);
    updateEditFormats({ fontSize: nextSize.toString() });
  };

  const handleAlignmentChange = async (index: number) => {
    if (textObject) {
      const textAlignment = (ALIGNMENT_OPTIONS[index.toString()] || 'left').toLowerCase();
      const wasApplied = await applyTextOptions({ textAlign: textAlignment });
      if (!wasApplied) return;
      setAlignmentIndex(index);
      updateEditFormats({ textAlignment: textAlignment });
    }
  };

  useEffect(() => {
    const fontCollection = formatFontCollection(initializedData?.font_collection.fonts);
    if (fontCollection) {
      setFonts(fontCollection);
    }
  }, [initializedData?.font_collection.fonts]);

  return (
    <div className={styles['text-tab']} data-testid="font-tab">
      <FontStyle
        addClass={styles['font-style']}
        label={t('fontDrawer.fontStyle')}
        fonts={fonts}
        selectedFont={selectedFont}
        onChange={(option) => handleFontStyleChange(option as FontSelectedOption)}
      />
      <div className={styles['counter-alignment-container']}>
        {shouldShowFontResizeControls && (
          <FontSize
            addClass={styles['font-size']}
            label={t('fontDrawer.fontSize')}
            onClickButton={handleFontSizeChange}
            fontSizeValue={fontSizeValue}
            upper={60}
            lower={5}
            testId="font-size"
          />
        )}
        <Alignment
          label={t('fontDrawer.alignment')}
          addClass={styles.alignment}
          buttonGroupClick={handleAlignmentChange}
          activeIndex={alignmentIndex}
        />
      </div>
    </div>
  );
};
